import { FormEvent, KeyboardEvent, ReactNode } from "react";
import { AlertCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FieldValues, useFormContext, useFormState } from "react-hook-form";

import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import { FormSection } from "./types";
import { FormSectionHeading } from "./FormSectionHeading";
import { UseFormWizardReturn } from "./useFormWizard";
import { WizardStepper } from "./WizardStepper";

/**
 * Mensagens de erro de um campo do RHF, incluindo as de campos ANINHADOS
 * embaixo dele. Um `step.fields` como `['planilha']` (objeto) não tem
 * `.message` própria — quem falha é `planilha.planilhaNome` etc., um nível
 * abaixo — então uma leitura rasa (`errors.planilha?.message`) nunca acha
 * nada e o passo nunca é marcado como bloqueado. `message`/`type`/`types`/
 * `ref` são chaves do próprio `FieldError` do RHF, não sub-campos — paradas
 * na recursão, não fontes de mensagem adicional.
 */
function collectFieldMessages(node: unknown): string[] {
  if (!node || typeof node !== "object") return [];
  const obj = node as Record<string, unknown>;
  if (typeof obj.message === "string") return [obj.message];

  return Object.entries(obj)
    .filter(([key]) => !["type", "types", "ref"].includes(key))
    .flatMap(([, value]) => collectFieldMessages(value));
}

interface WizardShellProps<T extends FieldValues> {
  steps: FormSection<T>[];
  wizard: UseFormWizardReturn;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  /** Botão de envio renderizado no último passo (mantém tooltip/loading do form). */
  submitSlot: ReactNode;
  isSubmitting?: boolean;
  /** Por passo: há campo pendente/inválido? Repassado ao stepper. */
  errorSteps?: boolean[];
  /**
   * Mostra o `submitSlot` no rodapé de **todos** os passos (não só no último).
   * Usado na **edição**: o usuário mexe em poucos campos e quer salvar de onde
   * estiver, sem percorrer até o fim. Na criação fica `false` (o envio é o
   * encerramento natural do fluxo, só no último passo).
   */
  persistentSubmit?: boolean;
}

/**
 * Casca visual de um formulário em etapas: stepper + corpo do passo atual +
 * rodapé de navegação (Voltar / Próximo / enviar). Agnóstica de domínio — o
 * conteúdo de cada passo vem de `steps[i].content`.
 */
export function WizardShell<T extends FieldValues>({
  steps,
  wizard,
  onSubmit,
  onCancel,
  submitSlot,
  isSubmitting,
  errorSteps,
  persistentSubmit,
}: WizardShellProps<T>) {
  const step = steps[wizard.currentStep];
  const prefersReducedMotion = useReducedMotion();

  // Transição direcional entre passos: o novo entra do lado para onde o usuário
  // navega e o anterior sai pelo oposto. Com `prefers-reduced-motion` cai num
  // fade puro (sem deslocamento). `mode="wait"` evita os dois passos sobrepostos.
  const slide = prefersReducedMotion ? 0 : 24;
  const stepVariants = {
    enter: (dir: number) => ({ opacity: 0, x: dir * slide }),
    center: { opacity: 1, x: 0 },
    exit: (dir: number) => ({ opacity: 0, x: dir * -slide }),
  };

  // Pendências do passo atual: as próprias mensagens do schema dos campos
  // declarados em `step.fields`. Alimentam o tooltip do "Próximo".
  const { control } = useFormContext<T>();
  const { errors } = useFormState<T>({ control });
  const stepErrors = (step.fields ?? []).flatMap((field) =>
    collectFieldMessages((errors as Record<string, unknown>)[field as string]),
  );
  const isStepBlocked = stepErrors.length > 0;

  // Enter avança para o próximo passo (em vez de enviar cedo). No último passo
  // deixa o submit normal acontecer; em textarea/botões preserva o Enter nativo.
  const handleKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== "Enter" || wizard.isLast) return;
    const tag = (e.target as HTMLElement).tagName;
    if (tag === "TEXTAREA" || tag === "BUTTON") return;
    e.preventDefault();
    void wizard.goNext();
  };

  return (
    <Card className="border border-border bg-card shadow-sm">
      {/* Passo único (ex.: edição com o alvo travado): stepper de uma bolinha só
          não informa nada — o cabeçalho do passo já diz onde o usuário está. */}
      {steps.length > 1 && (
        <CardHeader className="border-b border-border bg-muted/5 px-4 py-5 sm:px-6">
          <WizardStepper
            steps={steps}
            currentStep={wizard.currentStep}
            furthestStep={wizard.furthestStep}
            onStepClick={wizard.goToStep}
            errorSteps={errorSteps}
          />
        </CardHeader>
      )}

      <form onSubmit={onSubmit} onKeyDown={handleKeyDown}>
        <CardContent className="px-4 pb-8 pt-6 sm:px-6">
          {/* Cabeçalho do passo atual */}
          <div className="mb-6">
            <FormSectionHeading title={step.title} description={step.description} />
          </div>

          {/* Apenas o passo atual é montado; o RHF preserva os valores dos demais */}
          <AnimatePresence mode="wait" custom={wizard.direction} initial={false}>
            <motion.div
              key={step.id}
              custom={wizard.direction}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {step.content}
            </motion.div>
          </AnimatePresence>
        </CardContent>

        <CardFooter className="sticky bottom-0 z-10 flex flex-col-reverse gap-3 rounded-b-xl border-t border-border bg-card/95 px-4 py-4 backdrop-blur supports-[backdrop-filter]:bg-card/80 sm:static sm:flex-row sm:items-center sm:justify-between sm:bg-muted/10 sm:px-6">
          {wizard.isFirst ? (
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              className="w-full text-muted-foreground hover:text-foreground sm:w-auto"
            >
              Cancelar
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={wizard.goBack}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              <ArrowLeft className="size-4" /> Voltar
            </Button>
          )}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
            {!wizard.isLast && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger
                    className={buttonVariants({
                      variant: isStepBlocked
                        ? "destructive"
                        : persistentSubmit
                          ? "outline"
                          : "default",
                      className: "w-full sm:w-auto sm:min-w-[120px]",
                    })}
                    type="button"
                    onClick={() => wizard.goNext()}
                  >
                    {isStepBlocked && <AlertCircle className="size-4" />}
                    Próximo <ArrowRight className="size-4" />
                  </TooltipTrigger>
                  {isStepBlocked && (
                    <TooltipContent side="top" className="max-w-[260px]">
                      <p className="mb-1 flex items-center gap-1.5 font-medium">
                        <AlertCircle className="size-3.5 shrink-0" />
                        Revise para avançar
                      </p>
                      <ul className="list-disc space-y-0.5 pl-4 text-xs">
                        {stepErrors.slice(0, 4).map((message, index) => (
                          <li key={index}>{message}</li>
                        ))}
                      </ul>
                      {stepErrors.length > 4 && (
                        <p className="mt-1 text-xs opacity-80">e mais {stepErrors.length - 4}…</p>
                      )}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Salvar: sempre no último passo; em todos quando `persistentSubmit`.
                No mobile o botão ocupa a largura toda (não estoura o card). */}
            {(wizard.isLast || persistentSubmit) && (
              <div className="w-full sm:w-auto [&>*]:w-full sm:[&>*]:w-auto">{submitSlot}</div>
            )}
          </div>
        </CardFooter>
      </form>
    </Card>
  );
}
